import gql from 'graphql-tag';
import cloneDeep from 'lodash/cloneDeep';
import 'moment-timezone';
import { compose, graphql } from 'react-apollo';
import fragments from 'utils/graphql/fragments';
import {
  formatOnlineOffers,
  formatPartnerRewards,
  formatPatronRewards
} from './formatting';

const PATRON_REWARDS_QUERY = gql`
  query {
    rewardsWithTimeZoneV2 {
      patronRewards {
        rewardTimeZoneHelpText
        patronRewards {
          prize {
            id
            name
            description
            category
            url
          }
          prizeCode
          onlineOfferUuid
          prizeType
          prizeName
          startDate
          endDate
          startDateUTC
          endDateUTC
          redeemedDateUTC
          redeemed
          maxPrizesPerPeriod
          isNewOffer
          isSoftOffer
        }
      }
      onlineOffers {
        ...OnlineOfferFields
      }
      partnerRewards {
        timeZoneHelpText
        partnerRewards {
          prize {
            id
            name
            description
            locations
            category
            subCategory
            url
          }
          amount
          amountInCents
          redeemCode
          event {
            eventType
            eventDate
          }
          redeemLink
          startDate
          endDate
          lastModifiedDate
          startDateUTC
          endDateUTC
          lastModifiedDateUTC
        }
      }
    }
  }
  ${fragments.onlineOffers}
`;

const REDEEM_ONLINE_OFFER = gql`
  mutation redeemOnlineOffer($input: RedeemOnlineOfferInput!) {
    redeemOnlineOffer(input: $input) {
      offerUuid
      success
      message
    }
  }
`;

const RESET_NEW_OFFERS = gql`
  mutation resetNew {
    removeNewOffers
  }
`;

const SUBSCRIPTION_NEW_REWARDS = gql`
  subscription NewPrizes {
    newPrizes {
      patronRewards {
        rewardTimeZoneHelpText
        patronRewards {
          prize {
            id
            name
            description
            category
            url
          }
          prizeCode
          onlineOfferUuid
          prizeType
          prizeName
          startDate
          endDate
          startDateUTC
          endDateUTC
          redeemedDateUTC
          redeemed
          maxPrizesPerPeriod
          isNewOffer
          isSoftOffer
        }
      }
      onlineOffers {
        onlineOffers {
          name
          offerUuid
          patronAccountNumber
          cmUserId
          offerState
          startDate
          endDate
          redeemedDate
          startDateUTC
          endDateUTC
          redeemedDateUTC
          isNew
          offerType
          subCategory
          amount
          redeemedAmount
          description
          source
        }
        total
      }
      partnerRewards {
        timeZoneHelpText
        partnerRewards {
          prize {
            id
            name
            description
            locations
            category
            subCategory
            url
          }
          amount
          amountInCents
          redeemCode
          event {
            eventType
            eventDate
          }
          redeemLink
          startDate
          endDate
          lastModifiedDate
          startDateUTC
          endDateUTC
          lastModifiedDateUTC
        }
      }
    }
  }
`;

export default compose(
  graphql(PATRON_REWARDS_QUERY, {
    options: { fetchPolicy: 'cache-and-network' },
    props: ({ data, ownProps }) => {
      const rewardsWithTimeZone = data.rewardsWithTimeZoneV2;
      const patronRewardsData =
        rewardsWithTimeZone?.patronRewards?.patronRewards ?? [];
      const onlineRewardsData =
        rewardsWithTimeZone?.onlineOffers?.onlineOffers ?? [];
      const partnerRewardsData =
        rewardsWithTimeZone?.partnerRewards?.partnerRewards ?? [];
      const patronRewards = formatPatronRewards(patronRewardsData);
      const onlineOffers = formatOnlineOffers(onlineRewardsData);
      const partnerRewards = formatPartnerRewards(partnerRewardsData);

      return {
        patronRewardsData: data,
        patronRewards,
        onlineOffers,
        partnerRewards,
        subscribeToNewRewards: () => {
          return data.subscribeToMore({
            document: SUBSCRIPTION_NEW_REWARDS,
            updateQuery: (prev, { subscriptionData }) => {
              if (!subscriptionData.data) return prev;

              const {
                patronRewards: newPatronRewards,
                onlineOffers: newOnlineOffers,
                partnerRewards: newPartnerRewards
              } = subscriptionData.data;

              const {
                rewardsWithTimeZoneV2: {
                  patronRewards: { patronRewards: prevPatronRewards },
                  onlineOffers: { onlineOffers: prevOnlineOffers },
                  partnerRewards: { partnerRewards: prevPartnerRewards }
                }
              } = prev;

              const updatedPatronRewards =
                newPatronRewards.patronRewards.length > 0
                  ? [
                      ...prevPatronRewards,
                      ...newPatronRewards.patronRewards.filter(
                        ({ prizeCode }) =>
                          !prevPatronRewards.some(
                            ({ prizeCode: existingPrizeCode }) =>
                              prizeCode === existingPrizeCode
                          )
                      )
                    ]
                  : prevPatronRewards;

              const updatedOnlineOffers =
                newOnlineOffers.onlineOffers.length > 0
                  ? [
                      ...prevOnlineOffers,
                      ...newOnlineOffers.onlineOffers.filter(
                        ({ prizeCode }) =>
                          !prevOnlineOffers.some(
                            ({ prizeCode: existingPrizeCode }) =>
                              prizeCode === existingPrizeCode
                          )
                      )
                    ]
                  : prevOnlineOffers;

              const updatedPartnerRewards =
                newPartnerRewards.partnerRewards.length > 0
                  ? [
                      ...prevPartnerRewards,
                      ...newPartnerRewards.partnerRewards.filter(
                        ({ redeemCode }) =>
                          !prevPartnerRewards.some(
                            ({ redeemCode: existingRedeemCode }) =>
                              redeemCode === existingRedeemCode
                          )
                      )
                    ]
                  : prevPartnerRewards;

              return {
                ...prev,
                rewardsWithTimeZoneV2: {
                  ...prev.rewardsWithTimeZoneV2,
                  patronRewards: {
                    ...prev.rewardsWithTimeZoneV2.patronRewards,
                    patronRewards: updatedPatronRewards
                  },
                  onlineOffers: {
                    ...prev.rewardsWithTimeZoneV2.onlineOffers,
                    onlineOffers: updatedOnlineOffers
                  },
                  partnerRewards: {
                    ...prev.rewardsWithTimeZoneV2.partnerRewards,
                    partnerRewards: updatedPartnerRewards
                  }
                }
              };
            }
          });
        }
      };
    }
  }),
  graphql(REDEEM_ONLINE_OFFER, {
    props: ({ ownProps, mutate }) => ({
      redeemOnlineOffer: (offerUuid, amount) =>
        mutate({
          variables: {
            input: { offerUuid, amount }
          },
          update: (cache, response) => {
            if (!response.data.redeemOnlineOffer.success) {
              return;
            }
            const data = cache.readQuery({
              query: PATRON_REWARDS_QUERY
            });

            let rewards = cloneDeep(
              data.rewardsWithTimeZoneV2.onlineOffers.onlineOffers
            );
            let found = rewards.find(r => r.onlineOfferUuid === offerUuid);
            if (found) {
              found.redeemedAmount = amount;
            }
            data.rewardsWithTimeZoneV2.onlineOffers.onlineOffers = rewards;
            cache.writeQuery({
              query: PATRON_REWARDS_QUERY,
              data
            });
          }
        })
    })
  }),
  graphql(RESET_NEW_OFFERS, {
    props: ({ ownProps, mutate }) => ({
      resetNewState: () =>
        mutate({
          update: (cache, response) => {
            const data = cache.readQuery({
              query: PATRON_REWARDS_QUERY
            });

            let rewards = [
              ...data.rewardsWithTimeZoneV2.patronRewards.patronRewards
            ];
            rewards.forEach(r => (r.isNewOffer = false));
            data.rewardsWithTimeZoneV2.patronRewards.patronRewards = rewards;
            cache.writeQuery({
              query: PATRON_REWARDS_QUERY,
              data
            });
          }
        })
    })
  })
);
